import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { isVisualizacao } from '../utils/permissoes';
import { Bike, Tag, Coins, TrendingUp, Clock } from 'lucide-react';
import CompradoDe from '../components/CompradoDe';
import { baixarContratoCompra, podeContratoCompra } from '../utils/contratoCompra';

const fmt = v => 'R$ ' + parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
const inpStyle = { width: '100%', padding: '8px 10px', background: '#F4F6F9', border: '0.5px solid rgba(26,58,92,0.15)', borderRadius: '7px', color: '#1C2533', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", boxSizing: 'border-box' };
const lbl = { color: '#6B7A8D', fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.3px' };
const th = { padding: '0 12px 10px 0', color: '#9AA3AE', fontSize: '10.5px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', borderBottom: '0.5px solid rgba(26,58,92,0.1)' };
const td = { padding: '11px 12px 11px 0', borderBottom: '0.5px solid rgba(26,58,92,0.07)', fontSize: '13px', color: '#1C2533', verticalAlign: 'middle' };

// KPIs do topo (mesmo visual do Dashboard): azul-marinho, texto branco, ícone em círculo
const kpiCard = { background: '#1A2332', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '18px', color: '#fff', minWidth: 0 };
function KpiEstoque({ Icone, cor, titulo, valor, sub }) {
  return (
    <div style={kpiCard}>
      <div style={{ fontSize: '11px', fontWeight: '600', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '12px' }}>{titulo}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.06)', borderRadius: '8px', padding: '12px 10px' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: cor + '2E', color: cor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icone size={18} strokeWidth={2.2} /></div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '17px', fontWeight: '700', fontFamily: "'DM Mono',monospace", whiteSpace: 'nowrap' }}>{valor}</div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>
        </div>
      </div>
    </div>
  );
}

const statusBadge = (s) => {
  const m = { estoque: { bg: '#EAF5EE', c: '#2D7D4F' }, vendido: { bg: '#F4F6F9', c: '#6B7A8D' }, consignado: { bg: '#FFF3E0', c: '#B06000' } };
  const d = m[s] || { bg: '#F4F6F9', c: '#6B7A8D' };
  return { background: d.bg, color: d.c, padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' };
};

const diasBadge = (d) => {
  const n = parseInt(d || 0);
  return { display: 'inline-block', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: n < 20 ? '#EAF5EE' : n < 35 ? '#FFF3E0' : '#FDECEA', color: n < 20 ? '#2D7D4F' : n < 35 ? '#B06000' : '#C0392B' };
};

export default function Estoque() {
  const [veiculos, setVeiculos] = useState([]);
  const [busca, setBusca] = useState('');
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [mostrarVendidos, setMostrarVendidos] = useState(false);
  const navigate = useNavigate();

  const excluirVeiculo = async (id, nome) => {
    if (!window.confirm(`Excluir ${nome}? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete('/veiculos/' + id);
      carregar();
    } catch(e) { alert(e.response?.data?.error || 'Erro ao excluir'); }
  };
  const { usuario } = useAuth();
  const isMobile = window.innerWidth < 768;
  const somenteLeitura = isVisualizacao(usuario); // visualização vê como admin, sem ações
  const isAdmin = usuario?.role === 'admin' || usuario?.nivel === 'admin' || usuario?.nivel === 'gerente' || somenteLeitura;
  const podeEditar = isAdmin && !somenteLeitura;

  useEffect(() => { carregar(); }, []);

  const carregar = () => api.get('/veiculos').then(r => setVeiculos(r.data));

  // Coluna SITE: alterna publicar_site direto da lista (rota PATCH própria, não o PUT completo)
  const [salvandoSite, setSalvandoSite] = useState(null);
  const alternarSite = async (v) => {
    if (somenteLeitura || salvandoSite) return;
    const novo = !v.publicar_site;
    setSalvandoSite(v.id);
    try {
      const r = await api.patch('/veiculos/' + v.id + '/site', { publicar_site: novo });
      setVeiculos(vs => vs.map(x => x.id === v.id ? { ...x, publicar_site: r.data.publicar_site } : x));
    } catch (e) { alert(e.response?.data?.error || 'Erro ao alterar publicação no site'); }
    finally { setSalvandoSite(null); }
  };

  const diasEstoque = (v) => {
    if (!v.criado_em) return 0;
    return Math.floor((new Date() - new Date(v.criado_em)) / 86400000);
  };

  // Resumo do topo: veículos não vendidos (a mesma base que a tabela mostra por padrão),
  // com as mesmas contas das colunas "Custo Total" e "Margem". Só exibição.
  const emEstoque = veiculos.filter(v => v.status !== 'vendido');
  const somaVenda = emEstoque.reduce((s, v) => s + parseFloat(v.valor_venda || 0), 0);
  const somaCusto = emEstoque.reduce((s, v) => s + parseFloat(v.valor_compra || 0) + parseFloat(v.total_despesas || 0), 0);
  const margemTotal = somaVenda - somaCusto;
  const parados30 = emEstoque.filter(v => diasEstoque(v) > 30).length;

  const filtrados = veiculos.filter(v =>
    (mostrarVendidos || v.status !== 'vendido') &&
    (v.marca?.toLowerCase().includes(busca.toLowerCase()) ||
      v.modelo?.toLowerCase().includes(busca.toLowerCase()) ||
      v.placa?.toLowerCase().includes(busca.toLowerCase()))
  );

  // Consignado com investidor: VALOR COMPRA = investidor + loja (só a parte da loja vai pro A Pagar).
  const comRateio = form.tipo_entrada === 'consignado com investidor';
  const somaRateio = (parseFloat(form.valor_investidor) || 0) + (parseFloat(form.valor_loja) || 0);

  const abrirEdicao = (v) => {
    setForm({ ...v, valor_loja: v.tipo_entrada === 'consignado com investidor'
      ? (parseFloat(v.valor_compra || 0) - parseFloat(v.valor_investidor || 0)).toFixed(2) : '' });
    setModal(true);
  };

  const salvar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const dados = { ...form };
      if (comRateio) {
        dados.valor_investidor = parseFloat(form.valor_investidor) || 0;
        dados.valor_loja = parseFloat(form.valor_loja) || 0;
        dados.valor_compra = somaRateio;
      } else {
        delete dados.valor_loja;
      }
      if (form.id) await api.put('/veiculos/' + form.id, dados);
      else await api.post('/veiculos', dados);
      setModal(false); setForm({}); carregar();
    } catch (err) { alert(err.response?.data?.error || 'Erro'); }
    finally { setLoading(false); }
  };

  const inp = (label, campo, tipo, opts) => (
    <div style={{ marginBottom: '12px' }}>
      <label style={lbl}>{label}</label>
      {opts ? (
        <select value={form[campo] || ''} onChange={e => setForm({ ...form, [campo]: e.target.value })} style={inpStyle}>
          <option value=''>Selecione</option>
          {opts.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={tipo || 'text'} value={form[campo] || ''} onChange={e => setForm({ ...form, [campo]: e.target.value })} style={inpStyle} />
      )}
    </div>
  );

  // Colunas dependendo do nível
  const colunasAdmin = ['Marca / Modelo', 'Cor', 'Chassi', 'Ano', 'Placa', 'KM', 'Compra', 'Venda', 'Custo Total', 'Margem', 'Dias', 'Status', 'Site', ''];
  const colunasVendedor = ['Marca / Modelo', 'Cor', 'Chassi', 'Ano', 'Placa', 'KM', 'Preço de Venda', 'Dias', 'Status', 'Site', ''];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ fontSize: '18px', fontWeight: '700', color: '#1C2533' }}>
          Estoque <span style={{ fontSize: '14px', color: '#6B7A8D', fontWeight: '400' }}>({filtrados.length} veículos)</span>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label style={{ color: '#6B7A8D', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input type='checkbox' checked={mostrarVendidos} onChange={e => setMostrarVendidos(e.target.checked)} />
            Mostrar vendidos
          </label>
          {podeEditar && (
            <button onClick={() => { setForm({ tipo: 'usado', tipo_entrada: 'compra', status: 'estoque' }); setModal(true); }}
              style={{ padding: '9px 18px', background: '#1A3A5C', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
              + Novo Veículo
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '20px', marginBottom: '20px' }}>
        <KpiEstoque Icone={Bike} cor='#F39C12' titulo='Veículos em estoque' valor={emEstoque.length} sub={!isAdmin ? 'disponíveis para venda' : parados30 > 0 ? `${parados30} parado(s) há +30 dias` : 'nenhum parado há +30 dias'} />
        <KpiEstoque Icone={Tag} cor='#4D8DFF' titulo='Valor de venda' valor={fmt(somaVenda)} sub='soma dos preços de venda' />
        {isAdmin ? (
          <>
            <KpiEstoque Icone={Coins} cor='#A78BFA' titulo='Custo total' valor={fmt(somaCusto)} sub='compra + despesas' />
            <KpiEstoque Icone={TrendingUp} cor='#34C77B' titulo='Margem potencial' valor={fmt(margemTotal)} sub={somaVenda > 0 ? `${Math.round((margemTotal / somaVenda) * 100)}% sobre a venda` : '—'} />
          </>
        ) : (
          <KpiEstoque Icone={Clock} cor='#E74C3C' titulo='Parados +30 dias' valor={parados30} sub='veículos para priorizar' />
        )}
      </div>

      <div style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '10px 14px', marginBottom: '14px' }}>
        <input placeholder='Buscar por marca, modelo ou placa...' value={busca} onChange={e => setBusca(e.target.value)}
          style={{ width: '100%', background: 'transparent', border: 'none', fontSize: '14px', color: '#1C2533', outline: 'none', fontFamily: "'DM Sans',system-ui,sans-serif" }} />
      </div>

      <div style={{ background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '16px 18px' }}>
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {filtrados.length === 0 && <div style={{ textAlign: 'center', color: '#9AA3AE', padding: '32px 0' }}>Nenhum veículo encontrado</div>}
            {filtrados.map(v => {
              const dias = diasEstoque(v);
              const custo = parseFloat(v.valor_compra || 0) + parseFloat(v.total_despesas || 0);
              const margem = parseFloat(v.valor_venda || 0) - custo;
              return (
                <div key={v.id} style={{ background: '#fff', borderRadius: '12px', padding: '14px 16px', border: '0.5px solid rgba(26,58,92,0.1)', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
                  onClick={() => navigate('/estoque/' + v.id)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: '#1C2533' }}>{v.marca} {v.modelo}</div>
                      <div style={{ fontSize: '12px', color: '#9AA3AE' }}>{v.ano_mod} · {v.tipo} · {(v.km||0).toLocaleString()} km</div>
                    </div>
                    <span style={{ background: v.status === 'estoque' ? '#EAF5EE' : '#FDECEA', color: v.status === 'estoque' ? '#2D7D4F' : '#C0392B', padding: '3px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>{v.status}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', paddingTop: '8px', borderTop: '0.5px solid rgba(26,58,92,0.08)' }}>
                    <div style={{ fontSize: '16px', fontWeight: '800', color: '#2D7D4F' }}>{fmt(v.valor_venda)}</div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span style={{ background: dias > 30 ? '#FDECEA' : '#F4F6F9', color: dias > 30 ? '#C0392B' : '#6B7A8D', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>{dias}d</span>
                      {isAdmin && <span style={{ fontSize: '12px', color: margem > 0 ? '#2D7D4F' : '#C0392B', fontWeight: '600' }}>Margem: {fmt(margem)}</span>}
                      {isAdmin && podeContratoCompra(v) && (
                        <button onClick={e => { e.stopPropagation(); baixarContratoCompra(v.id); }}
                          style={{ padding: '3px 8px', background: 'transparent', border: '1px solid #185FA5', color: '#185FA5', borderRadius: '6px', fontSize: '11px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                          Contrato
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {(isAdmin ? colunasAdmin : colunasVendedor).map(h => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtrados.length === 0 && <tr><td colSpan={14} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '32px 0' }}>Nenhum veículo encontrado</td></tr>}
            {filtrados.map(v => {
              const dias = diasEstoque(v);
              const custo = parseFloat(v.valor_compra || 0) + parseFloat(v.total_despesas || 0);
              const margem = parseFloat(v.valor_venda || 0) - custo;
              return (
                <tr key={v.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/estoque/' + v.id)}>
                  <td style={td}>
                    <div style={{ fontWeight: '500' }}>{v.marca} {v.modelo}</div>
                    <div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '1px' }}>{v.tipo}</div>
                  </td>
                  <td style={{ ...td, color: '#6B7A8D', fontSize: '12px' }}>{v.cor || '—'}</td>
                  <td style={{ ...td, color: '#6B7A8D', fontFamily: "'DM Mono',monospace", fontSize: '11px' }}>{v.chassi ? v.chassi.slice(-8) : '—'}</td>
                  <td style={{ ...td, color: '#6B7A8D' }}>{v.ano_mod}</td>
                  <td style={{ ...td, color: '#6B7A8D', fontFamily: "'DM Mono',monospace", fontSize: '12px' }}>{v.placa || '—'}</td>
                  <td style={{ ...td, color: '#6B7A8D', fontFamily: "'DM Mono',monospace", fontSize: '12px' }}>{(v.km || 0).toLocaleString()}</td>

                  {isAdmin && <>
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px' }}>{fmt(v.valor_compra)}</td>
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px', color: '#2D7D4F', fontWeight: '600' }}>{fmt(v.valor_venda)}</td>
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px', color: '#1E5FA5' }}>{fmt(custo)}</td>
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px', color: margem > 0 ? '#2D7D4F' : '#C0392B', fontWeight: '600' }}>{fmt(margem)}</td>
                  </>}

                  {!isAdmin && (
                    <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px', color: '#2D7D4F', fontWeight: '600' }}>{fmt(v.valor_venda)}</td>
                  )}

                  <td style={td}><span style={diasBadge(dias)}>{dias}d</span></td>
                  <td style={td}><span style={statusBadge(v.status)}>{v.status}</span></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    <span onClick={() => alternarSite(v)}
                      title={somenteLeitura ? undefined : (v.publicar_site ? 'Clique para remover do site' : 'Clique para publicar no site')}
                      style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', userSelect: 'none',
                        background: v.publicar_site ? '#EAF5EE' : '#F4F6F9', color: v.publicar_site ? '#2D7D4F' : '#6B7A8D',
                        cursor: somenteLeitura ? 'default' : (salvandoSite === v.id ? 'wait' : 'pointer'), opacity: salvandoSite === v.id ? 0.6 : 1 }}>
                      {v.publicar_site ? 'No site' : 'Fora do site'}
                    </span>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    {podeEditar && (
                      <>
                      <button onClick={e => { e.stopPropagation(); excluirVeiculo(v.id, v.marca + ' ' + v.modelo); }}
                        style={{ padding: '4px 10px', background: '#FDECEA', border: 'none', color: '#C0392B', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600', marginRight: '4px' }}>
                        Excluir
                      </button>
                      <button onClick={e => { e.stopPropagation(); abrirEdicao(v); }}
                        style={{ padding: '4px 10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#1A3A5C', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontFamily: "'DM Sans',system-ui,sans-serif", marginRight: '6px' }}>
                        Editar
                      </button>
                      </>
                    )}
                    {isAdmin && podeContratoCompra(v) && (
                      <button onClick={e => { e.stopPropagation(); baixarContratoCompra(v.id); }} title='Gerar Contrato de Compra'
                        style={{ padding: '4px 10px', background: 'transparent', border: '1px solid #185FA5', color: '#185FA5', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontFamily: "'DM Sans',system-ui,sans-serif", marginRight: '6px' }}>
                        Contrato Compra
                      </button>
                    )}
                    <button onClick={e => { e.stopPropagation(); navigate('/estoque/' + v.id); }}
                      style={{ padding: '4px 10px', background: 'transparent', border: '1px solid #1E5FA5', color: '#1E5FA5', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                      Ver
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </div>

      {/* Modal — só admin */}
      {modal && podeEditar && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px 32px', width: '620px', maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1C2533', margin: 0 }}>{form.id ? 'Editar' : 'Novo'} Veículo</h3>
              <button onClick={() => { setModal(false); setForm({}); }} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#9AA3AE', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={salvar}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                {inp('Marca', 'marca')} {inp('Modelo', 'modelo')}
                {inp('Ano Fab', 'ano_fab', 'number')} {inp('Ano Mod', 'ano_mod', 'number')}
                {inp('Cor', 'cor')} {inp('Combustível', 'combustivel', 'text', ['Gasolina', 'Flex', 'Etanol', 'Diesel', 'Elétrico'])}
                {/el[eé]tric/i.test(form.combustivel || '') && <>
                  {inp('Tipo de Bateria', 'num_bateria', 'text', ['Lítio', 'Chumbo'])} {inp('Autonomia (km)', 'autonomia')}
                  {inp('NF-e', 'numero_nfe')}
                </>}
                {inp('Chassi', 'chassi')} {inp('Renavam', 'renavam')}
                {inp('Placa', 'placa')} {inp('KM', 'km', 'number')}
                {inp('Tipo', 'tipo', 'text', ['usado', 'novo'])}
                {inp('Entrada', 'tipo_entrada', 'text', ['compra', 'troca', 'consignado', 'consignado com investidor'])}
                {form.tipo_entrada === 'compra' && (
                  <CompradoDe value={form.comprado_de_cliente_id} onChange={id => setForm(f => ({ ...f, comprado_de_cliente_id: id }))} />
                )}
                {comRateio && <>
                  {inp('Valor Investidor', 'valor_investidor', 'number')} {inp('Valor Loja', 'valor_loja', 'number')}
                </>}
                {comRateio ? (
                  <div style={{ marginBottom: '12px' }}>
                    <label style={lbl}>Valor Compra</label>
                    <input type='number' value={somaRateio.toFixed(2)} readOnly title='Valor Investidor + Valor Loja'
                      style={{ ...inpStyle, background: '#E9EDF2', color: '#6B7A8D', cursor: 'not-allowed' }} />
                  </div>
                ) : inp('Valor Compra', 'valor_compra', 'number')}
                {inp('Valor Venda', 'valor_venda', 'number')}
                {inp('Status', 'status', 'text', ['estoque', 'vendido', 'consignado'])}
              </div>
              {inp('Descrição', 'descricao')}
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button type='button' onClick={() => { setModal(false); setForm({}); }}
                  style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '8px', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                  Cancelar
                </button>
                <button type='submit' disabled={loading}
                  style={{ flex: 1, padding: '10px', background: '#1A3A5C', border: 'none', color: '#fff', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontFamily: "'DM Sans',system-ui,sans-serif" }}>
                  {loading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
